import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    faBars,
    faLink,
    faBell,
    faBug,
    faSync,
    faChevronUp,
    faChevronDown,
    faBarcode,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { ValueHelperModel } from '../../models/low-level/value-helper.model';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { MenuEntryModel } from './menu-entry.model';
import { SideMenuModel } from './side-menu.model';
import { SideMenuService } from './side-menu.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SubscribePushNotificationVisualGuideComponent } from '../subscribe-push-notifications-visual-guide/subscribe-push-notifications-visual-guide.component';
import { ClientManagerService } from '../../services/client-manager/client-manager.service';
import { ReportIssueComponent } from '../report-issue/report-issue.component';
import { ReportIssueModel } from '../report-issue/report-issue.model';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { getHrefBasePath } from '../../utilities/url-utilities';
import { BarcodeClipboardModalComponent } from '../barcode-clipboard-modal/barcode-clipboard-modal.component';
import { Subject } from 'rxjs';

@Component({
    selector: 'pace-core-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit, OnDestroy {
    @Input()
    public model: SideMenuModel;

    public faBars = faBars;
    public hasBeenOpened = false;
    public isOpen = false;
    public furtherLinksEntry: MenuEntryModel;
    public furtherLinks: ValueHelperModel[] = [];
    public showFurtherLinks = false;
    public pushNotificationEntry: MenuEntryModel;
    public restartAppEntry: MenuEntryModel;
    public reportIssueEntry: MenuEntryModel;
    public barcodeClipboardEntry: MenuEntryModel;
    public isPWADisplayMode = false;
    public faChevronDown = faChevronDown;
    public faChevronUp = faChevronUp;
    public isCollapsed = true;
    private destroy$ = new Subject<void>();
    public constructor(
        private readonly translate: TranslateService,
        private readonly valueHelperService: ValueHelperService,
        private readonly sideMenuService: SideMenuService,
        private readonly modalService: BsModalService,
        private readonly clientManagerService: ClientManagerService,
    ) {
        this.model = this.sideMenuService.getModel();
        this.furtherLinks = this.valueHelperService.getFurtherLinks();
        this.furtherLinksEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_FURTHER_LINKS_LABEL'),
            () => this.openFurtherLinks(),
            faLink,
        );
        this.pushNotificationEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_SUBSCRIBE_TO_NOTIFICATIONS'),
            () => this.openSubscribeNotificationsVisualGuide(),
            faBell,
        );

        this.restartAppEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_RESTART_APP'),
            () => this.reloadApplication(),
            faSync,
        );

        this.reportIssueEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_REPORT_ISSUE_LABEL'),
            () => this.openReportIssue(),
            faBug,
        );

        this.barcodeClipboardEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_BARCODE_CLIPBOARD_LABEL'),
            () => this.openBarcodeClipboard(),
            faBarcode,
        );
    }

    public ngOnInit(): void {
        this.checkIfPWADisplayMode();
        this.addVisibilityChangeListener();
    }

    public toggleSideMenu(): void {
        this.showFurtherLinks = false;
        this.isOpen = !this.isOpen;
        // enable closing of the menu
        this.hasBeenOpened = true;
    }

    private openFurtherLinks(): void {
        this.showFurtherLinks = !this.showFurtherLinks;
    }

    private checkIfPWADisplayMode(): void {
        this.isPWADisplayMode = this.clientManagerService.isPacePWA();
    }

    // Rechecks for display mode when user switches from webapp to pwa standalone app.
    // Used to display Subscribe to Notifications functionality and modals only in PWA mode.
    private addVisibilityChangeListener(): void {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkIfPWADisplayMode();
            }
        });
    }

    public openSubscribeNotificationsVisualGuide(): void {
        this.isOpen = !this.isOpen;
        this.modalService.show(SubscribePushNotificationVisualGuideComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'subscribe-push-notification-modal-title',
        });
    }

    private openReportIssue(): void {
        this.modalService.show(ReportIssueComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new ReportIssueModel(),
            },
            ariaLabelledBy: 'report-issue-modal-title',
        });
    }

    private openBarcodeClipboard(): void {
        this.isOpen = false;
        this.modalService.show(BarcodeClipboardModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {},
            ignoreBackdropClick: true,
            ariaLabelledBy: 'barcode-clipboard-modal-title',
        });
    }

    private reloadApplication(): void {
        window.location.href = getHrefBasePath(window.location.href);
    }

    public toggleFurtherLinks(): void {
        this.isCollapsed = !this.isCollapsed;
        this.openFurtherLinks();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
